import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import styles from './layout.less';

const LAYOUT_DIRECTION = ['row', 'column'];

const defaults = {
  alignItems: 'stretch',
  alignSelf: 'auto',
  className: '',
  direction: 'row',
  displayFlex: true,
  element: 'div',
  gutter: true,
  gutterSm: false,
  gutterTop: true,
  gutterRight: true,
  gutterBottom: true,
  gutterLeft: true,
  justifyContent: 'flex-start',
  scroll: false,
};

const layoutFactory = (options = {}) => {
  // Take the default options for the factory and the options passed in during construction
  // and merge together, assigning the resulting object keys to the default constants.
  const {
    alignItems: defaultAlignItems,
    alignSelf: defaultAlignSelf,
    className: defaultClassName,
    direction: defaultDirection,
    displayFlex: defaultDisplayFlex,
    element: defaultElement,
    gutter: defaultGutter,
    gutterSm: defaultGutterSm,
    gutterTop: defaultGutterTop,
    gutterRight: defaultGutterRight,
    gutterBottom: defaultGutterBottom,
    gutterLeft: defaultGutterLeft,
    justifyContent: defaultJustifyContent,
    scroll: defaultScroll,
  } = { ...defaults, ...options };

  const Layout = forwardRef(
    (
      {
        alignItems,
        alignSelf,
        children,
        className,
        direction,
        displayFlex,
        element,
        flex,
        flexBasis,
        gutter,
        gutterSm,
        gutterTop,
        gutterRight,
        gutterBottom,
        gutterLeft,
        height,
        justifyContent,
        scroll,
        style,
        width,
        ...other
      },
      ref
    ) => {
      const _className = classnames(
        styles.layout,
        {
          [styles['layout-display-flex']]: displayFlex,
          [styles[`layout-direction-${direction}`]]: displayFlex && direction,
          [styles['layout-gutter']]: gutter,
          [styles['layout-gutter-sm']]: gutterSm,
          [styles['layout-no-gutter-top']]: !gutterTop,
          [styles['layout-no-gutter-right']]: !gutterRight,
          [styles['layout-no-gutter-bottom']]: !gutterBottom,
          [styles['layout-no-gutter-left']]: !gutterLeft,
          [styles['layout-scroll']]: scroll,
        },
        className
      );

      const _style = {
        flex,
        flexBasis,
        alignSelf,
        ...(height !== undefined ? { height } : {}),
        ...(width !== undefined ? { width } : {}),
        ...(displayFlex ? { alignItems, justifyContent } : {}),
        ...style,
      };

      const _props = {
        ...other,
        ref,
        className: _className,
        style: _style,
      };

      return React.createElement(element, _props, children);
    }
  );

  Layout.propTypes = {
    alignItems: PropTypes.string,
    alignSelf: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    direction: PropTypes.oneOf(LAYOUT_DIRECTION),
    displayFlex: PropTypes.bool.isRequired,
    element: PropTypes.string.isRequired,
    flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flexBasis: PropTypes.string,
    justifyContent: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gutter: PropTypes.bool.isRequired,
    gutterSm: PropTypes.bool,
    gutterTop: PropTypes.bool,
    gutterRight: PropTypes.bool,
    gutterBottom: PropTypes.bool,
    gutterLeft: PropTypes.bool,
    scroll: PropTypes.bool,
    style: PropTypes.object,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  Layout.defaultProps = {
    alignItems: defaultAlignItems,
    alignSelf: defaultAlignSelf,
    className: defaultClassName,
    direction: defaultDirection,
    displayFlex: defaultDisplayFlex,
    element: defaultElement,
    gutter: defaultGutter,
    gutterSm: defaultGutterSm,
    gutterTop: defaultGutterTop,
    gutterRight: defaultGutterRight,
    gutterBottom: defaultGutterBottom,
    gutterLeft: defaultGutterLeft,
    justifyContent: defaultJustifyContent,
    scroll: defaultScroll,
    flexBasis: 'auto',
  };

  return Layout;
};

export default layoutFactory;
export { layoutFactory };
